<script setup lang="ts">
import {
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  VisuallyHidden,
} from 'reka-ui'
import { cn } from '@ds/lib/utils'
import { XIcon } from '@lucide/vue'

type Props = {
  open: boolean
  title: string
}

const { open, title } = defineProps<Props>()

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()
</script>

<template>
  <DialogRoot
    :open
    @update:open="emit('update:open', $event)"
  >
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      />
      <DialogContent
        :class="cn(
          'fixed inset-x-0 bottom-0 z-50 max-h-[85vh]',
          'font-ds flex flex-col bg-card text-card-foreground rounded-t-ds-xl shadow-ds-xl',
          'pb-[max(1rem,env(safe-area-inset-bottom))]',
          'duration-ds-normal data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        )"
      >
        <VisuallyHidden as-child>
          <DialogDescription>{{ title }}</DialogDescription>
        </VisuallyHidden>
        <div class="flex justify-center pt-2 pb-1 shrink-0">
          <span
            class="w-9 h-1.25 rounded-full bg-muted"
            aria-hidden="true"
          />
        </div>
        <div class="flex items-center gap-2 pl-5 pr-2 pb-2 shrink-0">
          <DialogTitle class="flex-1 text-lg font-bold text-foreground truncate">
            {{ title }}
          </DialogTitle>
          <button
            type="button"
            class="flex items-center justify-center size-11 rounded-ds-md text-muted-foreground hover:bg-background cursor-pointer"
            :aria-label="`Close ${title}`"
            @click="emit('update:open', false)"
          >
            <XIcon
              class="size-5"
              aria-hidden="true"
            />
          </button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto">
          <slot />
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
